<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { ST } from '@/constants/ST'
import { TP_MOVIMENTO } from '@/constants/TP_MOVIMENTO'
import { CATFIN_TP_MOV } from '@/constants/CATFIN_TP_MOV'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { criarConfigBuscaPerfilTributario } from '@/search-definitions/perfilTributarioSearch'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()

// Controlo de Acesso (RBAC)
const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('FISCAL_OPERACOES_COMPLETO'))
})

// Lógica de Personificação e Crachá Dinâmico
const nivelFiscal = ref('CARREGANDO')
const nivelFiscalDescricao = ref('A Carregar...')

// --- Plano de Categoria Financeira vigente ---
const idPlanoCategoriaFinanceira = ref(null)

/** Resolve o plano de categoria financeira ativo e vigente para a data atual */
const resolverPlanoCategoria = async () => {
  try {
    const res = await api.get('/PlanosCategoriasFinanceiras', {
      params: {
        lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Ativo,
        idEmpresa: authStore.empresaAtiva,
        pageSize: 50
      }
    })
    const hoje = new Date()
    const plano = (res.data || []).find(p => {
      if (!p.dataInicioVigencia || !p.dataFimVigencia) return false
      const inicio = new Date(p.dataInicioVigencia + 'T00:00:00')
      const fim = new Date(p.dataFimVigencia + 'T00:00:00')
      return hoje >= inicio && hoje <= fim
    })
    idPlanoCategoriaFinanceira.value = plano ? plano.idPlanoCategoriaFinanceira : null
    if (!idPlanoCategoriaFinanceira.value) {
      console.warn('[OperacoesCadastro] Nenhum plano ativo vigente encontrado para a data atual.')
    }
  } catch (e) {
    console.warn('[OperacoesCadastro] Erro ao resolver plano vigente:', e)
    idPlanoCategoriaFinanceira.value = null
  }
}

// Listas Auxiliares (Soft Delete)
const listas = reactive({
  status: [],
  tipoMovimento: []
})

const carregarListas = async () => {
  try {
    const [resStatus, resTipoMovimento, resTipoOpMov] = await Promise.allSettled([
      api.get('/ListasSistema/lookup/ST'),
      api.get('/ListasSistema/lookup/TP_MOVIMENTO'),
      api.get('/ListasSistema/lookup/ESTQ_TIPO_OPERACAO_MOV')
    ])

    if (resStatus.status === 'fulfilled') listas.status = resStatus.value.data
    if (resTipoMovimento.status === 'fulfilled') listas.tipoMovimento = resTipoMovimento.value.data
    if (resTipoOpMov.status === 'fulfilled') listas.ESTQ_TIPO_OPERACAO_MOV = resTipoOpMov.value.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const carregarNivelFiscal = async () => {
  let nivelCodigo = NV_MODULO_FISCAL.Nenhum
  nivelFiscalDescricao.value = 'A Carregar...'

  try {
    if (authStore.isAdminInterno) {
      if (authStore.empresaAtiva) {
        const resEmpresa = await api.get(`/Empresas/${authStore.empresaAtiva}`)
        const idPlano = resEmpresa.data.idPlanoVigente || resEmpresa.data.IdPlanoVigente

        if (idPlano) {
          const resPlano = await api.get(`/Planos/${idPlano}`)
          nivelCodigo = resPlano.data.lstNivelModuloFiscal || resPlano.data.LstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
        }
      }
    } else {
      nivelCodigo = authStore.lstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
    }
  } catch (e) {
    // Silenciado
  }

  nivelFiscal.value = String(nivelCodigo).toUpperCase()

  try {
    if (nivelCodigo !== NV_MODULO_FISCAL.Nenhum) {
      const resLista = await api.get('/ListasSistema/lookup/NV_MODULO_FISCAL')
      const itemLista = resLista.data.find(i =>
        String(i.idListaSistemaItem).toUpperCase() === nivelFiscal.value ||
        String(i.textoLista).toUpperCase() === nivelFiscal.value
      )
      nivelFiscalDescricao.value = itemLista ? itemLista.textoLista : nivelFiscal.value
    } else {
      nivelFiscalDescricao.value = 'Nenhum'
    }
  } catch (e) {
    nivelFiscalDescricao.value = nivelFiscal.value
  }
}

// Avaliação Inteligente do Nível Fiscal
const isPlanoEssential = computed(() => {
  const code = nivelFiscal.value.toUpperCase()
  const desc = nivelFiscalDescricao.value.toUpperCase()

  if (code === NV_MODULO_FISCAL.Nenhum || desc === 'A CARREGAR...') return true

  return code.includes(NV_MODULO_FISCAL.Essential) || code.includes('ESSENCIAL') ||
         desc.includes(NV_MODULO_FISCAL.Essential) || desc.includes('ESSENCIAL')
})

// GUARDIÃO: Se a operação for um Serviço (Não movimenta estoque), os CFOPs nunca são obrigatórios!
const isCfopObrigatorio = computed(() => !isPlanoEssential.value && item.value.movimentaEstoque === true)

// Toggle de Tipo de CFOP: Nacional vs Internacional
// Apenas visual (não persiste no backend). Ao alternar, limpa os campos do grupo oposto.
const tipoCfop = ref('Nacional') // 'Nacional' | 'Internacional'

// Watch: ao alternar o toggle, limpa os campos do grupo oposto (destrutivo)
watch(tipoCfop, (novoValor, valorAntigo) => {
  if (novoValor === 'Nacional' && valorAntigo === 'Internacional') {
    item.value.cfopExterior = ''
  } else if (novoValor === 'Internacional' && valorAntigo === 'Nacional') {
    item.value.cfopDentroEstado = ''
    item.value.cfopForaEstado = ''
  }
})

// Dinâmica de exigência individual dos CFOPs baseada no toggle:
// - 'Nacional' → CFOP Dentro Estado e Fora Estado são obrigatórios (se aplicável)
// - 'Internacional' → CFOP Exterior é obrigatório (se aplicável)
const isCfopDentroEstadoRequired = computed(() => isCfopObrigatorio.value && tipoCfop.value === 'Nacional')
const isCfopForaEstadoRequired = computed(() => isCfopObrigatorio.value && tipoCfop.value === 'Nacional')
const isCfopExteriorRequired = computed(() => isCfopObrigatorio.value && tipoCfop.value === 'Internacional')

const simNaoOptions = [
  { id: true, descricao: 'Sim' },
  { id: false, descricao: 'Não' }
]

const confBuscaCat = computed(() => {
  const isEntrada = item.value.lstTipoMovimento === TP_MOVIMENTO.Entrada
  // Entrada → categorias de Custo/Despesa/Imposto/N/A; Saída → categorias de Receita/N/A
  const tiposMovimento = isEntrada
    ? `${CATFIN_TP_MOV.Custo},${CATFIN_TP_MOV.Despesa},${CATFIN_TP_MOV.Imposto},${CATFIN_TP_MOV.NA}`
    : `${CATFIN_TP_MOV.Receita},${CATFIN_TP_MOV.NA}`

  return criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira Padrão',
    tiposMovimento,
    ST.Ativo,
    authStore.organizacaoAtiva,
    null,
    idPlanoCategoriaFinanceira.value,
    true
  )
})

const { item, isLoading, isEditing, entityInfo, submitForm, initializeForm, handleCancel } = useFormulario(props, emit, { idKey: 'idOperacao' })

// Gestão da Auditoria
const mostrarAuditoria = ref(false)

// Estado dos Accordions (Frames)
const accordionState = reactive({
  dadosBasicos: true,
  comportamentos: true,
  padroesFiscais: true
})

// Search Definition para Perfil Tributário (filtra apenas Ativos)
const confBuscaPerfilTributario = computed(() => {
  if (!authStore.organizacaoAtiva) return null
  return criarConfigBuscaPerfilTributario(authStore.organizacaoAtiva)
})

// Texto do Status para exibir no cabeçalho
const statusTexto = computed(() => {
  if (!item.value.lstStatus) return ''
  const found = (listas.status || []).find(s => String(s.idListaSistemaItem) === String(item.value.lstStatus))
  return found ? found.textoLista : item.value.lstStatus
})

// Computed que desabilita os campos filhos quando GeraFinanceiro=false
const financeiroDisabled = computed(() => !item.value.geraFinanceiro || !podeEditar.value)

// Computed que desabilita os campos filhos quando MovimentaEstoque=false
const estoqueDisabled = computed(() => !item.value.movimentaEstoque || !podeEditar.value)

// GUARDIÃO: a Categoria Financeira padrão da Operação é obrigatória quando gera Líquido Fornecedor.
// Ela é a regra PRIORITÁRIA para o TítuloFinanceiroItem do Líquido a Pagar (a do produto é fallback).
const isCategoriaFinanceiraObrigatoria = computed(() => item.value.geraLiquidoFornecedor === true)

function validarCategoriaFinanceira () {
  if (isCategoriaFinanceiraObrigatoria.value && !item.value.idCategoriaFinanceira) {
    emit('show-notification', {
      message: 'A Categoria Financeira Padrão é obrigatória quando a Operação gera Líquido Fornecedor.',
      type: 'warning'
    })
    return false
  }
  return true
}

function onChangeGeraFinanceiro (value) {
  if (!value) {
    item.value.geraTributos = false
    item.value.geraLiquidoFornecedor = false
    item.value.idCategoriaFinanceira = null
  }
}

function onChangeMovimentaEstoque (value) {
  if (!value) {
    item.value.lstTipoOperacaoEstoque = ''
  }
}

async function salvar () {
  if (!podeEditar.value) return
  if (!validarCategoriaFinanceira()) return

  const payload = {
    ...item.value,
    lstStatus: item.value.lstStatus || null,
    idCategoriaFinanceira: item.value.idCategoriaFinanceira || null,
    idPerfilTributarioPadrao: item.value.idPerfilTributarioPadrao || null
  }

  await submitForm('Operacoes', 'idOperacao', payload)
}

async function salvarAlteracoes () {
  if (!podeEditar.value) return
  if (!validarCategoriaFinanceira()) return

  const isNew = !item.value.idOperacao
  const payload = {
    ...item.value,
    lstStatus: item.value.lstStatus || null,
    idCategoriaFinanceira: item.value.idCategoriaFinanceira || null,
    idPerfilTributarioPadrao: item.value.idPerfilTributarioPadrao || null
  }

  // Remove o ID do payload em novos cadastros para evitar conflito de PK
  if (isNew) {
    delete payload.idOperacao
  }

  try {
    isLoading.value = true
    const res = isNew
      ? await api.post('/Operacoes', payload)
      : await api.put(`/Operacoes/${item.value.idOperacao}`, payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    let msg = 'Erro ao salvar alterações.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

watch(() => authStore.empresaAtiva, () => {
  carregarNivelFiscal()
  resolverPlanoCategoria()
})

// Quando o Tipo de Movimento muda, reseta a Categoria Financeira selecionada
// para evitar inconsistência (ex: categoria de Receita selecionada numa operação de Saída)
watch(() => item.value.lstTipoMovimento, () => {
  item.value.idCategoriaFinanceira = null
})

// Função que detecta o Tipo de CFOP baseado nos valores preenchidos
// Usada tanto na inicialização quanto em mudanças futuras
function detectarTipoCfop () {
  if (item.value.cfopExterior) {
    tipoCfop.value = 'Internacional'
  } else if (item.value.cfopDentroEstado || item.value.cfopForaEstado) {
    tipoCfop.value = 'Nacional'
  }
  // Se ambos vazios, mantém o padrão ('Nacional')
}

// Watch no idOperacao para detectar o tipo ao carregar/alterar registro
watch(() => item.value.idOperacao, (novoId) => {
  if (novoId) {
    detectarTipoCfop()
  }
})

onMounted(() => {
  carregarNivelFiscal()
  carregarListas()
  resolverPlanoCategoria()
  // Garante a detecção ao abrir a tela (o watch acima só pega mudanças futuras)
  detectarTipoCfop()
})
</script>

<template>
  <FormularioCadastro
    module-name="Operação Fiscal"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :accordion-state="accordionState"
    :status="statusTexto"
    @submit="salvar"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
    :ocultar-salvar="!podeEditar"
  >
    <template #acoes-header>
      <span class="badge bg-dark border border-secondary-subtle px-3 py-2 me-3 d-flex align-items-center" style="font-size: 0.85rem;">
        <i class="bi bi-shield-check me-2 text-info"></i> Nível Fiscal: {{ nivelFiscalDescricao }}
      </span>

      <!-- Botão para acionar Auditoria -->
      <button
        v-if="isEditing"
        type="button"
        class="btn-icon-minimalista me-3"
        title="Ver Histórico de Alterações"
        @click="mostrarAuditoria = true"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- Frame: Dados Básicos -->
    <div class="card mb-4 shadow-sm border-0">
      <div
        class="card-header bg-transparent border-bottom-0 pb-0 pt-3 cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.dadosBasicos = !accordionState.dadosBasicos"
      >
        <h6 class="mb-0 text-body-emphasis">Dados Básicos</h6>
        <i :class="['bi', accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down']" class="text-body-secondary"></i>
      </div>
      <div class="card-body" v-show="accordionState.dadosBasicos">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="descricao"
              label="Descrição da Operação"
              v-model="item.descricao"
              placeholder="Ex: Venda de Mercadoria"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoSelect
              id="lstTipoMovimento"
              label="Tipo de Movimento"
              v-model="item.lstTipoMovimento"
              :options="listas.tipoMovimento"
              option-value="idListaSistemaItem"
              option-label="textoLista"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoSelect
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              :options="listas.status"
              option-value="idListaSistemaItem"
              option-label="textoLista"
              required
              :disabled="!podeEditar"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Frame: Comportamentos no ERP -->
    <div class="card mb-4 shadow-sm border-0">
      <div
        class="card-header bg-transparent border-bottom-0 pb-0 pt-3 cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.comportamentos = !accordionState.comportamentos"
      >
        <h6 class="mb-0 text-body-emphasis">Comportamentos no ERP</h6>
        <i :class="['bi', accordionState.comportamentos ? 'bi-chevron-up' : 'bi-chevron-down']" class="text-body-secondary"></i>
      </div>
      <div class="card-body" v-show="accordionState.comportamentos">
        <!-- Linha 1: GeraFinanceiro + Configurações Financeiras -->
        <div class="row g-3">
          <div class="col-md-3">
            <CampoSelect
              id="geraFinanceiro"
              label="Gera Integração Financeira?"
              v-model="item.geraFinanceiro"
              :options="simNaoOptions"
              option-value="id"
              option-label="descricao"
              required
              :disabled="!podeEditar"
              @update:model-value="onChangeGeraFinanceiro"
            />
          </div>

          <div class="col-md-9">
            <!-- Miniframe: Configurações Financeiras (dependente de GeraFinanceiro) -->
            <div
              class="p-3 rounded-3 border h-100"
              :class="item.geraFinanceiro ? 'bg-light-subtle border-primary-subtle' : 'bg-body-tertiary border-secondary-subtle opacity-50'"
            >
              <div class="d-flex align-items-center mb-2">
                <i class="bi bi-currency-dollar me-2" :class="item.geraFinanceiro ? 'text-primary' : 'text-secondary'"></i>
                <span class="small fw-semibold" :class="item.geraFinanceiro ? 'text-primary' : 'text-secondary'">
                  Configurações Financeiras
                </span>
                <span v-if="!item.geraFinanceiro" class="badge bg-secondary ms-2" style="font-size: 0.65rem;">BLOQUEADO</span>
                <span v-else class="badge bg-primary ms-2" style="font-size: 0.65rem;">ATIVO</span>
              </div>
              <div class="row g-3">
                <div class="col-md-4">
                  <CampoSelect
                    id="geraTributos"
                    label="Gera Tributos?"
                    v-model="item.geraTributos"
                    :options="simNaoOptions"
                    option-value="id"
                    option-label="descricao"
                    required
                    :disabled="financeiroDisabled"
                  />
                </div>
                <div class="col-md-4">
                  <CampoSelect
                    id="geraLiquidoFornecedor"
                    label="Gera Líquido Fornecedor?"
                    v-model="item.geraLiquidoFornecedor"
                    :options="simNaoOptions"
                    option-value="id"
                    option-label="descricao"
                    required
                    :disabled="financeiroDisabled"
                  />
                </div>
                <div class="col-md-4">
                  <CampoBusca
                    id="idCategoriaFinanceira"
                    label="Categoria Financeira Padrão"
                    v-model="item.idCategoriaFinanceira"
                    :configuracao-busca="confBuscaCat"
                    :required="isCategoriaFinanceiraObrigatoria"
                    :disabled="financeiroDisabled"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Linha 2: MovimentaEstoque + Configurações de Estoque -->
        <div class="row g-3 mt-3">
          <div class="col-md-3">
            <CampoSelect
              id="movimentaEstoque"
              label="Movimenta Estoque Físico?"
              v-model="item.movimentaEstoque"
              :options="simNaoOptions"
              option-value="id"
              option-label="descricao"
              required
              :disabled="!podeEditar"
              @update:model-value="onChangeMovimentaEstoque"
            />
            <div class="form-text small text-info mt-1" v-if="item.movimentaEstoque === false">
              <i class="bi bi-info-circle-fill me-1"></i> Configuração correta para operações de <strong>Serviços</strong> ou estritamente financeiras.
            </div>
          </div>

          <div class="col-md-9">
            <!-- Miniframe: Configurações de Estoque (dependente de MovimentaEstoque) -->
            <div
              class="p-3 rounded-3 border h-100"
              :class="item.movimentaEstoque ? 'bg-light-subtle border-primary-subtle' : 'bg-body-tertiary border-secondary-subtle opacity-50'"
            >
              <div class="d-flex align-items-center mb-2">
                <i class="bi bi-box-seam me-2" :class="item.movimentaEstoque ? 'text-primary' : 'text-secondary'"></i>
                <span class="small fw-semibold" :class="item.movimentaEstoque ? 'text-primary' : 'text-secondary'">
                  Configurações de Estoque
                </span>
                <span v-if="!item.movimentaEstoque" class="badge bg-secondary ms-2" style="font-size: 0.65rem;">BLOQUEADO</span>
                <span v-else class="badge bg-primary ms-2" style="font-size: 0.65rem;">ATIVO</span>
              </div>
              <div class="row g-3">
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstTipoOperacaoEstoque"
                    label="Tipo de Operação de Estoque"
                    v-model="item.lstTipoOperacaoEstoque"
                    tabela="Operacao"
                    :disabled="estoqueDisabled"
                    :required="item.movimentaEstoque"
                  />
                  <div class="form-text small text-info mt-1" v-if="!item.movimentaEstoque">
                    <i class="bi bi-info-circle-fill me-1"></i> Necessário apenas quando "Movimenta Estoque" = Sim.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Perfil Tributário Padrão (Essential) -->
        <div class="row g-3 mt-3">
          <div class="col-md-3" v-if="isPlanoEssential">
            <CampoBusca
              id="idPerfilTributarioPadrao"
              label="Perfil Tributário Padrão"
              v-model="item.idPerfilTributarioPadrao"
              :configuracao-busca="confBuscaPerfilTributario"
              :disabled="!podeEditar"
              required
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Frame: Padrões Fiscais (CFOP) -->
    <div class="card shadow-sm border-0 mb-4" v-if="!isPlanoEssential">
      <div
        class="card-header bg-transparent border-bottom-0 pb-0 pt-3 cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.padroesFiscais = !accordionState.padroesFiscais"
      >
        <h6 class="mb-0 text-body-emphasis">Padrões Fiscais (CFOP)</h6>
        <i :class="['bi', accordionState.padroesFiscais ? 'bi-chevron-up' : 'bi-chevron-down']" class="text-body-secondary"></i>
      </div>
      <div class="card-body" v-show="accordionState.padroesFiscais">
        <!-- GUARDIÃO: Aviso de flexibilização do CFOP em operações de Serviços -->
        <div v-if="item.movimentaEstoque === false" class="alert alert-light border-info border-start border-4 small py-2 mb-3 text-body-secondary">
          <i class="bi bi-info-circle-fill text-info me-2"></i> Como esta operação <strong>não movimenta estoque físico</strong> (característica de Serviços), o preenchimento de CFOPs não é obrigatório.
        </div>

        <!-- Toggle: Nacional vs Internacional (btn-group) -->
        <div class="row g-3 mb-3">
          <div class="col-md-6">
            <label class="form-label">Tipo de CFOP</label>
            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn flex-fill"
                :class="tipoCfop === 'Nacional' ? 'btn-primary' : 'btn-outline-secondary'"
                :disabled="!podeEditar"
                @click="tipoCfop = 'Nacional'"
              >
                <i class="bi bi-braces me-1"></i> Nacional (Dentro / Fora Estado)
              </button>
              <button
                type="button"
                class="btn flex-fill"
                :class="tipoCfop === 'Internacional' ? 'btn-primary' : 'btn-outline-secondary'"
                :disabled="!podeEditar"
                @click="tipoCfop = 'Internacional'"
              >
                <i class="bi bi-globe me-1"></i> Internacional (Exterior)
              </button>
            </div>
            <div class="form-text small text-info mt-1">
              <i class="bi bi-info-circle-fill me-1"></i> Selecione <strong>Nacional</strong> para operações dentro do país ou <strong>Internacional</strong> para operações com o exterior.
            </div>
          </div>
        </div>

        <div class="row g-3">
          <template v-if="tipoCfop === 'Nacional'">
            <div class="col-md-6">
              <CampoInput
                id="cfopDentroEstado"
                label="CFOP (Dentro do Estado)"
                v-model="item.cfopDentroEstado"
                placeholder="Ex: 5102"
                tipo="text"
                maxlength="4"
                :required="isCfopDentroEstadoRequired"
                :disabled="!podeEditar"
              />
              <div class="form-text small">Inicia com 1 ou 5</div>
            </div>
            <div class="col-md-6">
              <CampoInput
                id="cfopForaEstado"
                label="CFOP (Fora do Estado)"
                v-model="item.cfopForaEstado"
                placeholder="Ex: 6102"
                tipo="text"
                maxlength="4"
                :required="isCfopForaEstadoRequired"
                :disabled="!podeEditar"
              />
              <div class="form-text small">Inicia com 2 ou 6</div>
            </div>
          </template>
          <template v-if="tipoCfop === 'Internacional'">
            <div class="col-md-12">
              <CampoInput
                id="cfopExterior"
                label="CFOP (Exterior)"
                v-model="item.cfopExterior"
                placeholder="Ex: 7102"
                tipo="text"
                maxlength="4"
                :required="isCfopExteriorRequired"
                :disabled="!podeEditar"
              />
              <div class="form-text small">Inicia com 3 ou 7</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <!-- Componente de Auditoria do Utilizador -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    @fechar="mostrarAuditoria = false"
    tabela="Operacao"
    contexto="tenant"
    :chave-primaria="item.idOperacao"
    :titulo-registro="item.descricao"
  />
</template>
